import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useAuth } from './hooks/useApi';
import Login from './components/Auth/Login';
import Register from './components/Auth/Register';
import ForgotPassword from './components/Auth/ForgotPassword';
import ResetPassword from './components/Auth/ResetPassword';
import Navigation from './components/Navigation/Navigation';
import Dashboard from './components/Dashboard/Dashboard';
import BarcodeScanner from './components/Scanner/BarcodeScanner';
import PointsSystem from './components/Points/PointsSystem';
import GiftCardRedemption from './components/Redemption/GiftCardRedemption';
import ChatRoom from './components/Community/ChatRoom';
import News from './components/Community/News';
import SpinWheel from './components/SpinWheel/SpinWheel';
import Catalog from './components/Catalog/Catalog';
import Inventory from './components/Inventory/Inventory';
import Notifications from './components/Notifications/Notifications';
import UserProfile from './components/Profile/UserProfile';
import Footer from './components/Footer/Footer';

const AppContainer = styled.div`
  min-height: 100vh;
  width: 100%;
  background: #050505;
  overflow-x: hidden;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  position: relative;
  
  /* Star Field Background Effect */
  &::before {
    content: '';
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-image: 
      radial-gradient(2px 2px at 20px 30px, white, transparent),
      radial-gradient(2px 2px at 60px 70px, white, transparent),
      radial-gradient(1px 1px at 50px 50px, white, transparent),
      radial-gradient(1px 1px at 130px 80px, white, transparent),
      radial-gradient(2px 2px at 90px 10px, white, transparent);
    background-size: 200px 200px;
    background-repeat: repeat;
    opacity: 0.4;
    z-index: 0;
    pointer-events: none;
  }
  
  * {
    box-sizing: border-box;
  }
`;

const MainContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  position: relative;
  z-index: 1;
`;

const LoadingScreen = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  min-height: 100vh;
  background: #050505;
  position: relative;
  gap: 2rem;

  /* Star Field Background Effect */
  &::before {
    content: '';
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-image:
      radial-gradient(2px 2px at 20px 30px, white, transparent),
      radial-gradient(2px 2px at 60px 70px, white, transparent),
      radial-gradient(1px 1px at 50px 50px, white, transparent),
      radial-gradient(1px 1px at 130px 80px, white, transparent),
      radial-gradient(2px 2px at 90px 10px, white, transparent);
    background-size: 200px 200px;
    background-repeat: repeat;
    opacity: 0.4;
    z-index: 0;
    pointer-events: none;
  }
`;

const TrophySpinner = styled.div`
  position: relative;
  z-index: 2;

  .trophy-container {
    position: relative;
    width: 180px;
    height: 180px;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .trophy-glow {
    position: absolute;
    width: 160px;
    height: 160px;
    background: radial-gradient(circle, rgba(255, 215, 0, 0.3) 0%, transparent 70%);
    border-radius: 50%;
    animation: pulse 2s ease-in-out infinite;
  }

  .trophy-icon {
    font-size: 8rem;
    position: relative;
    z-index: 3;
    filter: drop-shadow(0 0 30px rgba(255, 215, 0, 0.6))
            drop-shadow(0 0 60px rgba(255, 215, 0, 0.4));
    animation: spin 3s linear infinite;
  }

  @keyframes spin {
    0% { transform: rotateY(0deg); }
    100% { transform: rotateY(360deg); }
  }

  @keyframes pulse {
    0%, 100% {
      transform: scale(1);
      opacity: 0.6;
    }
    50% {
      transform: scale(1.2);
      opacity: 0.3;
    }
  }
`;

const LoadingText = styled.div`
  position: relative;
  z-index: 2;
  text-align: center;

  .title {
    font-size: 2.5rem;
    font-weight: 700;
    letter-spacing: 0.15em;
    background: linear-gradient(135deg, #FFD700 0%, #FFA500 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    margin-bottom: 1rem;
    animation: fadeInOut 2s ease-in-out infinite;
  }

  .subtitle {
    font-size: 1rem;
    color: rgba(255, 255, 255, 0.6);
    font-weight: 400;
    letter-spacing: 0.1em;
  }

  @keyframes fadeInOut {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.5; }
  }
`;

function App() {
  const { user, token, loading, logout, isAuthenticated, login, updateUserPoints } = useAuth();
  const [currentView, setCurrentView] = useState('scanner'); // Start with scanner by default
  const [authMode, setAuthMode] = useState('login'); // 'login', 'register', 'forgot-password', 'reset-password'
  const [requiresScan, setRequiresScan] = useState(true); // Assume scan required until proven otherwise
  const [scanStatusLoading, setScanStatusLoading] = useState(true);
  const [prefilledUsername, setPrefilledUsername] = useState(''); // Store username for login prefill

  // Check for reset password code in URL on mount
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const resetCode = urlParams.get('code');
    if (resetCode) {
      setAuthMode('reset-password');
    }
  }, []);

  // Check scan status from user data (already loaded in auth context)
  useEffect(() => {
    if (!isAuthenticated) {
      setScanStatusLoading(false);
      setRequiresScan(true);
      return;
    }

    // User data is already loaded via auth context - check hasScannedOnce field
    const hasScanned = user?.hasScannedOnce || false;
    setRequiresScan(!hasScanned);

    if (hasScanned) {
      setCurrentView((prev) => (prev === 'scanner' ? 'dashboard' : prev));
    } else {
      setCurrentView('scanner');
    }

    setScanStatusLoading(false);
  }, [isAuthenticated, user?.hasScannedOnce]);

  // Prevent navigation away from scanner if scan is required
  useEffect(() => {
    if (requiresScan && currentView !== 'scanner') {
      console.log('Preventing navigation - scan required');
      setCurrentView('scanner');
    }
  }, [currentView, requiresScan]);

  // Disable right-click and long-press context menu
  useEffect(() => {
    const handleContextMenu = (e) => {
      e.preventDefault();
      return false;
    };

    const handleTouchStart = (e) => {
      if (e.touches.length > 1) return; // Allow multi-touch
      e.target.longPressTimer = setTimeout(() => {
        // Prevent context menu on long press
        e.preventDefault();
      }, 500);
    };

    const handleTouchEnd = (e) => {
      if (e.target.longPressTimer) {
        clearTimeout(e.target.longPressTimer);
      }
    };

    const handleTouchMove = (e) => {
      if (e.target.longPressTimer) {
        clearTimeout(e.target.longPressTimer);
      }
    };

    // Add event listeners
    document.addEventListener('contextmenu', handleContextMenu);
    document.addEventListener('touchstart', handleTouchStart, { passive: false });
    document.addEventListener('touchend', handleTouchEnd);
    document.addEventListener('touchmove', handleTouchMove);

    // Cleanup
    return () => {
      document.removeEventListener('contextmenu', handleContextMenu);
      document.removeEventListener('touchstart', handleTouchStart);
      document.removeEventListener('touchend', handleTouchEnd);
      document.removeEventListener('touchmove', handleTouchMove);
    };
  }, []);

  // Scroll to top whenever the view changes
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [currentView]);

  const handleLogin = (userData, userToken) => {
    // The useAuth hook handles the login state automatically
  };

  const handleLogout = () => {
    logout();
  };

  const handleRegisterSuccess = (username) => {
    setPrefilledUsername(username);
    setAuthMode('login');
  };

  const renderCurrentView = () => {
    switch (currentView) {
      case 'dashboard':
        return <Dashboard onNavigate={setCurrentView} />;
      case 'scanner':
        return <BarcodeScanner onNavigate={setCurrentView} requiresScan={requiresScan} />;
      case 'points':
        return <PointsSystem onNavigate={setCurrentView} />;
      case 'redemption':
        return <GiftCardRedemption onNavigate={setCurrentView} />;
      case 'catalog':
        return <Catalog onNavigate={setCurrentView}/>;
      case 'chat':
        return <ChatRoom onNavigate={setCurrentView}/>;
      case 'news':
        return <News onNavigate={setCurrentView}/>;
      case 'community':
        return <ChatRoom onNavigate={setCurrentView}/>; // Default community page to chat for now
      case 'spin-wheel':
        return <SpinWheel onNavigate={setCurrentView} user={user} updateUserPoints={updateUserPoints} />;
      case 'inventory':
        return <Inventory onNavigate={setCurrentView} />;
      case 'notifications':
        return <Notifications onNavigate={setCurrentView} />;
      case 'profile':
        return <UserProfile onNavigate={setCurrentView} />;
      default:
        return <Dashboard onNavigate={setCurrentView} />;
    }
  };

  // Show loading screen while checking authentication
  if (loading) {
    return (
      <LoadingScreen>
        <TrophySpinner>
          <div className="trophy-container">
            <div className="trophy-glow"></div>
            <div className="trophy-icon">🏆</div>
          </div>
        </TrophySpinner>
        <LoadingText>
          <div className="title">GG TROPHY</div>
          <div className="subtitle">Loading your gaming rewards...</div>
        </LoadingText>
      </LoadingScreen>
    );
  }

  // Show loading screen while checking scan status
  if (isAuthenticated && scanStatusLoading) {
    return (
      <LoadingScreen>
        <TrophySpinner>
          <div className="trophy-container">
            <div className="trophy-glow"></div>
            <div className="trophy-icon">🏆</div>
          </div>
        </TrophySpinner>
        <LoadingText>
          <div className="title">GG TROPHY</div>
          <div className="subtitle">Checking access permissions...</div>
        </LoadingText>
      </LoadingScreen>
    );
  }

  // Show authentication screen if not logged in
  if (!isAuthenticated) {
    return (
      <>
        {authMode === 'login' && (
          <Login
            onLogin={handleLogin}
            authMode={authMode}
            setAuthMode={setAuthMode}
            onForgotPassword={() => setAuthMode('forgot-password')}
            prefilledUsername={prefilledUsername}
          />
        )}
        {authMode === 'register' && (
          <Register 
            onRegisterSuccess={handleRegisterSuccess} 
            authMode={authMode} 
            setAuthMode={setAuthMode} 
          />
        )}
        {authMode === 'forgot-password' && (
          <ForgotPassword 
            onBackToLogin={() => setAuthMode('login')}
          />
        )}
        {authMode === 'reset-password' && (
          <ResetPassword 
            onBackToLogin={() => setAuthMode('login')}
          />
        )}
      </>
    );
  }

  // Show main application
  return (
    <AppContainer>
      <Navigation
        user={user}
        currentView={currentView}
        onViewChange={setCurrentView}
        onLogout={handleLogout}
        requiresScan={requiresScan}
        lockedMode={requiresScan}
      />
      <MainContent>
        {renderCurrentView()}
      </MainContent>
      {!requiresScan && currentView !== 'chat' && currentView !== 'community' && <Footer />}
    </AppContainer>
  );
}

export default App;